/**
 * How and when Watch is allowed to interrupt you.
 *
 * THREE OF THE VERDICT'S FIXES POINT HERE — "turn on the email digest", "raise the
 * immediate tier floor", "unmute these families" — and none of them had anywhere to go.
 * The API has served and accepted all of it since phase 1; nothing rendered it.
 *
 * ACCOUNT-WIDE, WHICH IS WHY IT LIVES BESIDE THE WATCH LIST rather than on a subject. A
 * tier floor and a digest cadence are decisions about your inbox, not about one
 * competitor, and a copy of these controls per watch would make twelve places to
 * disagree.
 *
 * THE TIER FLOOR IS THE SHARPEST CONTROL HERE and the copy says what it does rather than
 * naming the tier: "only the most urgent" is a choice somebody can make, and
 * "immediate_tier_floor = SAME_DAY" is not. Nothing is lost by raising it — the quieter
 * alerts still arrive in the digest, which is the sentence that makes it safe to turn.
 */
import { useEffect, useState } from 'react';
import api from '../../services/api';
import type { WatchDigestCadence, WatchPreferences, WatchTier } from '../../types';

/** What each floor means for your inbox, in the terms the choice is actually about. */
const FLOORS: { value: WatchTier; label: string; hint: string }[] = [
  {
    value: 'IMMEDIATE',
    label: 'Only the most urgent',
    hint: 'A funding round, an acquisition, a rival bidding on your brand. Everything else waits for the digest.',
  },
  {
    value: 'SAME_DAY',
    label: 'Urgent and same-day',
    hint: 'Adds price moves, hiring for your channel, a page falling off page one.',
  },
  {
    value: 'DIGEST',
    label: 'Everything, as it happens',
    hint: 'Emails every alert immediately. Most of what a monitor finds is routine, so this is loud.',
  },
];

const CADENCES: { value: WatchDigestCadence; label: string }[] = [
  { value: 'DAILY', label: 'Every day' },
  { value: 'WEEKLY', label: 'Once a week' },
  { value: 'OFF', label: 'Never' },
];

export default function NotificationPrefs() {
  const [prefs, setPrefs] = useState<WatchPreferences | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .getWatchPreferences()
      .then((got) => {
        if (alive) setPrefs(got);
      })
      // Silent: these are settings, and a red banner where a form would go reads as
      // something being wrong with the watches themselves.
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  const change = async (patch: Partial<WatchPreferences>) => {
    setSaving(true);
    setError(null);
    try {
      setPrefs(await api.patchWatchPreferences(patch));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not save');
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) return null;

  return (
    <section className="mb-6" data-testid="watch-prefs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs font-semibold uppercase tracking-wide text-gray-500 underline dark:text-gray-400"
      >
        {open ? 'Hide notifications' : 'How you hear about this'}
      </button>

      {open && (
        <div className="mt-2 space-y-3">
          <label className="flex items-baseline gap-2">
            <input
              type="checkbox"
              checked={prefs.email_enabled}
              disabled={saving}
              onChange={(e) => void change({ email_enabled: e.target.checked })}
            />
            <span className="text-xs">
              Email me
              <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                Off, everything still arrives in the feed - but nobody visits a feed they
                are not reminded about.
              </span>
            </span>
          </label>

          <div>
            <h3 className="mb-1 text-[11px] font-semibold text-gray-600 dark:text-gray-300">
              What interrupts you
            </h3>
            <ul className="space-y-1">
              {FLOORS.map((floor) => (
                <li key={floor.value}>
                  <label className="flex items-baseline gap-2">
                    <input
                      type="radio"
                      name="watch-floor"
                      checked={prefs.immediate_tier_floor === floor.value}
                      disabled={saving || !prefs.email_enabled}
                      onChange={() =>
                        void change({ immediate_tier_floor: floor.value })
                      }
                    />
                    <span className="text-xs">
                      {floor.label}
                      <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                        {floor.hint}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              {/* The sentence that makes this safe to turn down. */}
              Raising this loses nothing - the quieter alerts still arrive in the digest.
            </p>
          </div>

          <label className="block">
            <span className="block text-[11px] font-semibold text-gray-600 dark:text-gray-300">
              Digest
            </span>
            <select
              value={prefs.digest_cadence}
              disabled={saving || !prefs.email_enabled}
              onChange={(e) =>
                void change({
                  digest_cadence: e.target.value as WatchDigestCadence,
                })
              }
              className="mt-0.5 rounded border border-gray-300 px-2 py-0.5 text-xs dark:border-gray-600 dark:bg-gray-900"
            >
              {CADENCES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block text-[11px] font-semibold text-gray-600 dark:text-gray-300">
              Most interrupting emails a day
            </span>
            <input
              type="number"
              min={0}
              max={50}
              value={prefs.max_immediate_per_day}
              disabled={saving || !prefs.email_enabled}
              onChange={(e) =>
                void change({ max_immediate_per_day: Number(e.target.value) })
              }
              className="mt-0.5 w-16 rounded border border-gray-300 px-2 py-0.5 text-xs dark:border-gray-600 dark:bg-gray-900"
            />
            <span className="mt-0.5 block text-[11px] text-gray-500 dark:text-gray-400">
              A hard cap. Past it the rest roll into the digest rather than being lost.
            </span>
          </label>

          {prefs.muted_families.length > 0 && (
            <div>
              <h3 className="mb-1 text-[11px] font-semibold text-gray-600 dark:text-gray-300">
                Muted everywhere
              </h3>
              {/* A family mute is silent on every watch, which is exactly why it has to be
                  visible: otherwise it is indistinguishable from a broken check. */}
              <ul className="flex flex-wrap gap-1">
                {prefs.muted_families.map((family) => (
                  <li key={family}>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() =>
                        void change({
                          muted_families: prefs.muted_families.filter(
                            (f) => f !== family,
                          ),
                        })
                      }
                      className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600 disabled:opacity-40 dark:bg-gray-800 dark:text-gray-300"
                      title="Unmute on every watch"
                    >
                      {family} ×
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error && (
            <p className="text-[11px] text-red-600 dark:text-red-400" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
